<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Sidebar -->
    <div class="w-80 bg-white border-l border-gray-200">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-xl font-bold text-gray-800 mb-4">الإشعارات</h2>
        
        <!-- Filter Tabs -->
        <div class="flex space-x-1 space-x-reverse bg-gray-100 rounded-lg p-1 mb-4">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="selectedTab = tab.id"
            :class="[
              'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
              selectedTab === tab.id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            ]"
          >
            {{ tab.name }}
            <span v-if="getTabCount(tab.id) > 0" class="mr-1">
              ({{ getTabCount(tab.id) }})
            </span>
          </button>
        </div>
      </div>

      <!-- Notifications List -->
      <div class="overflow-y-auto">
        <div
          v-for="notification in filteredNotifications"
          :key="notification.id"
          class="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
          @click="markAsRead(notification)"
        >
          <div class="flex items-start space-x-3 space-x-reverse">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              :class="getNotificationIconClass(notification.type)"
            >
              <component :is="getNotificationIcon(notification.type)" class="w-5 h-5" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between mb-1">
                <h3 class="font-semibold text-gray-800 text-sm">{{ notification.title }}</h3>
                <span class="text-xs text-gray-500">{{ formatTime(notification.time) }}</span>
              </div>
              <p class="text-gray-600 text-sm">{{ notification.content }}</p>
              <div v-if="notification.action" class="mt-2">
                <button
                  @click.stop="handleAction(notification)"
                  class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {{ notification.action.text }}
                </button>
              </div>
            </div>
            <div v-if="!notification.read" class="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 p-6">
      <div class="max-w-2xl mx-auto">
        <div v-if="selectedNotification" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <!-- Notification Header -->
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center space-x-3 space-x-reverse">
              <div
                class="w-12 h-12 rounded-full flex items-center justify-center"
                :class="getNotificationIconClass(selectedNotification.type)"
              >
                <component :is="getNotificationIcon(selectedNotification.type)" class="w-6 h-6" />
              </div>
              <div>
                <h1 class="text-xl font-bold text-gray-800">{{ selectedNotification.title }}</h1>
                <p class="text-sm text-gray-600">{{ formatTime(selectedNotification.time) }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2 space-x-reverse">
              <button
                @click="markAsRead(selectedNotification)"
                class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="تحديد كمقروء"
              >
                <Check class="w-5 h-5 text-gray-600" />
              </button>
              <button
                @click="deleteNotification(selectedNotification)"
                class="p-2 rounded-lg hover:bg-red-50 transition-colors"
                title="حذف"
              >
                <Trash2 class="w-5 h-5 text-red-600" />
              </button>
            </div>
          </div>

          <!-- Notification Content -->
          <div class="prose prose-sm max-w-none">
            <p class="text-gray-700 leading-relaxed">{{ selectedNotification.content }}</p>
            
            <!-- Action Buttons -->
            <div v-if="selectedNotification.action" class="mt-6 p-4 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600 mb-3">{{ selectedNotification.action.description }}</p>
              <div class="flex space-x-3 space-x-reverse">
                <button
                  @click="handleAction(selectedNotification)"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {{ selectedNotification.action.text }}
                </button>
                <button
                  @click="selectedNotification = null"
                  class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  تجاهل
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <Bell class="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p class="text-gray-600">اختر إشعار من القائمة لعرض التفاصيل</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Bell, MessageSquare, FileText, Check, Trash2, Calendar, AlertTriangle } from 'lucide-vue-next'

interface NotificationAction {
  text: string
  description: string
  url?: string
}

interface Notification {
  id: string
  title: string
  content: string
  type: 'message' | 'document' | 'appointment' | 'system' | 'alert'
  time: Date
  read: boolean
  action?: NotificationAction
}

const selectedTab = ref('all')
const selectedNotification = ref<Notification | null>(null)

const tabs = [
  { id: 'all', name: 'الكل' },
  { id: 'unread', name: 'غير مقروء' },
  { id: 'messages', name: 'الرسائل' },
  { id: 'documents', name: 'الوثائق' },
  { id: 'appointments', name: 'المواعيد' }
]

const notifications: Notification[] = [
  {
    id: '1',
    title: 'رسالة جديدة من المحامي أحمد',
    content: 'لقد قمت بمراجعة مستندات القضية وأرسلت لك ملاحظاتي. يرجى مراجعتها والرد في أقرب وقت ممكن.',
    type: 'message',
    time: new Date(Date.now() - 1000 * 60 * 30),
    read: false,
    action: {
      text: 'عرض الرسالة',
      description: 'انتقل إلى صفحة الرسائل لعرض المحادثة الكاملة.',
      url: '/messenger'
    }
  },
  {
    id: '2',
    title: 'تم تحديث وثيقة قانونية',
    content: 'تم إضافة تعديلات جديدة على قانون العمل الأردني. يرجى مراجعة التغييرات لتبقى على اطلاع بأحدث المستجدات.',
    type: 'document',
    time: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: true,
    action: {
      text: 'عرض الوثيقة',
      description: 'انتقل إلى المكتبة القانونية لعرض الوثيقة المحدثة.',
      url: '/library'
    }
  },
  {
    id: '3',
    title: 'موعد محكمة غداً',
    content: 'موعدك في محكمة العدل العليا غداً الساعة 10:00 صباحاً. لا تنسى إحضار جميع المستندات المطلوبة.',
    type: 'appointment',
    time: new Date(Date.now() - 1000 * 60 * 60 * 24),
    read: false,
    action: {
      text: 'عرض التفاصيل',
      description: 'عرض تفاصيل الموعد والموقع على الخريطة.',
      url: '/appointments'
    }
  },
  {
    id: '4',
    title: 'تنبيه أمان',
    content: 'تم تسجيل دخول جديد إلى حسابك من جهاز غير معروف. إذا كان هذا ليس أنت، يرجى تغيير كلمة المرور فوراً.',
    type: 'alert',
    time: new Date(Date.now() - 1000 * 60 * 60 * 48),
    read: false
  }
]

const filteredNotifications = computed(() => {
  let filtered = notifications

  switch (selectedTab.value) {
    case 'unread':
      filtered = filtered.filter(n => !n.read)
      break
    case 'messages':
      filtered = filtered.filter(n => n.type === 'message')
      break
    case 'documents':
      filtered = filtered.filter(n => n.type === 'document')
      break
    case 'appointments':
      filtered = filtered.filter(n => n.type === 'appointment')
      break
  }

  return filtered.sort((a, b) => b.time.getTime() - a.time.getTime())
})

const getTabCount = (tabId: string) => {
  switch (tabId) {
    case 'unread':
      return notifications.filter(n => !n.read).length
    case 'messages':
      return notifications.filter(n => n.type === 'message').length
    case 'documents':
      return notifications.filter(n => n.type === 'document').length
    case 'appointments':
      return notifications.filter(n => n.type === 'appointment').length
    default:
      return 0
  }
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'message': return MessageSquare
    case 'document': return FileText
    case 'appointment': return Calendar
    case 'alert': return AlertTriangle
    default: return Bell
  }
}

const getNotificationIconClass = (type: string) => {
  switch (type) {
    case 'message': return 'bg-blue-100 text-blue-600'
    case 'document': return 'bg-green-100 text-green-600'
    case 'appointment': return 'bg-purple-100 text-purple-600'
    case 'alert': return 'bg-red-100 text-red-600'
    default: return 'bg-gray-100 text-gray-600'
  }
}

const formatTime = (date: Date) => {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  
  if (diff < 1000 * 60) return 'الآن'
  if (diff < 1000 * 60 * 60) return `منذ ${Math.floor(diff / (1000 * 60))} دقيقة`
  if (diff < 1000 * 60 * 60 * 24) return `منذ ${Math.floor(diff / (1000 * 60 * 60))} ساعة`
  return `منذ ${Math.floor(diff / (1000 * 60 * 60 * 24))} يوم`
}

const markAsRead = (notification: Notification) => {
  notification.read = true
}

const deleteNotification = (notification: Notification) => {
  const index = notifications.findIndex(n => n.id === notification.id)
  if (index !== -1) {
    notifications.splice(index, 1)
    if (selectedNotification.value?.id === notification.id) {
      selectedNotification.value = null
    }
  }
}

const handleAction = (notification: Notification) => {
  if (notification.action?.url) {
    console.log('Navigate to:', notification.action.url)
    // TODO: Navigate to action URL
  }
}
</script>
